/**
 * Leviathan Web - Active Deliveries Widget
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 *
 * Shows count of active (EN_ROUTE, ARRIVED, DELIVERING) dispatches
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Activity } from 'lucide-react';
import { API_URL } from '@/config/api';

const ActiveDeliveriesWidget = () => {
  const { accessToken } = useAuth();
  const [activeCount, setActiveCount] = useState(0);
  const [totalToday, setTotalToday] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/dashboard`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (response.ok) {
          const data = await response.json();
          // Both figures must come from the SAME population or the fraction is
          // nonsense. This used to read `activeDispatches`, which has no date
          // filter, against `dispatchesToday`, which does — so it could render
          // "2 of 0 dispatches today".
          setActiveCount(data.dashboard?.kpis?.activeDispatchesToday ?? 0);
          setTotalToday(data.dashboard?.kpis?.dispatchesToday ?? 0);
        }
      } catch {
        // silent
      }
    };
    fetchData();
  }, [accessToken]);

  const percentage = totalToday > 0 ? Math.round((activeCount / totalToday) * 100) : 0;

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center space-x-2 mb-3">
        <Activity className="w-4 h-4 text-warning-amber" />
        <span className="text-[10px] uppercase tracking-[0.15em] text-text-secondary font-mono">
          Active Deliveries
        </span>
      </div>

      <div
        className="text-3xl font-mono font-bold text-warning-amber mb-1"
        style={{ textShadow: '0 0 15px rgba(251,191,36,0.6)' }}
      >
        {activeCount}
      </div>
      <div className="text-text-tertiary text-xs font-mono mb-4">
        of {totalToday} dispatches today
      </div>

      {/* Progress bar */}
      <div className="mt-auto">
        <div className="flex justify-between text-[10px] font-mono text-text-tertiary mb-1">
          <span>Active Rate</span>
          <span className="text-warning-amber">{percentage}%</span>
        </div>
        <div className="h-2 bg-background rounded-sm overflow-hidden">
          <div
            className="h-full bg-warning-amber transition-all"
            style={{
              width: `${percentage}%`,
              boxShadow: '0 0 6px rgba(251,191,36,0.6)',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ActiveDeliveriesWidget;
